/**
 * The FilterableContentProvider component acts as setter/getter for filters functionality and displaying results to views
 *
 * @module ui/providers/FilterableContentProvider/FilterableContentProvider
 */
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

import { useRetailerVisitorId } from '@ulta/core/hooks/useRetailerVisitorId/useRetailerVisitorId';
import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';
import { useUserContext } from '@ulta/core/providers/UserContextProvider/UserContextProvider';
import { handleRedirect, handleReplace } from '@ulta/core/utils/handleLocation/handleLocation';
import { scrollToElement } from '@ulta/core/utils/scrollToElement/scrollToElement';

import { FILTER_EVENT } from '@ulta/utils/constants/filters';

import * as utils from './FilterableContentProvider';

/**
 * Represents a FilterableContentProvider component
 *
 * @method
 * @param { Object } props - React properties passed from composition
 * @returns FilterableContentProvider
 */
export const FilterableContentProvider = function( props ){
  const { previewDate } = useAppConfigContext();

  const {
    children,
    loading = false,
    invokeAction,
    currentUrl,
    responseModuleParams: moduleParams,
    isInitialRequest = {}
  } = props;
  /**
   * A "filter request" is dispatched from any filter, sort, or load more UI interaction
   *
   * Model for a filter request:
   * event:      type of request, defined in constant `FILTER_EVENT`
   * action:     the DXL action associated with the UI element
   */
  const [filterRequest, setFilterRequest] = useState( {} );
  const mergeItems = useRef( false );

  // Previous request ID stores a composite request id to prevent duplicate requests
  const prevRequestId = useRef();
  const previousAction = useRef();

  // We use this wrapper ref for scrolling to the top of the element on filter selection
  const wrapperRef = useRef();

  // Gets user details + retailer cookie ID
  const { user } = useUserContext();
  const { retailerVisitorId } = useRetailerVisitorId();

  const wasLoading = useRef( false );
  useEffect( () => {
    if( !wasLoading.current && loading ){
      wasLoading.current = true;
    }

    if( wasLoading.current && isInitialRequest.current && loading === false ){
      isInitialRequest.current = false;
    }
  }, [loading] );

  // Listen to currentUrl from DXL and apply to client
  useEffect( () => {
    if( !currentUrl ){
      return;
    }

    const url = new URL( currentUrl );

    handleReplace( { url: url.href, previewDate, preserveParams: isInitialRequest.current } );
  }, [currentUrl] );

  // Listens for filter requests and will dispatch queries to graph ql
  useEffect( () => {
    const queryId = utils.getCompositeFilterQueryId( {
      filterRequest,
      user,
      retailerVisitorId
    } );

    // Composes an action based on the current state, handles business logic
    // and appending moduleParams for DXL
    const action = utils.getFilterAction( {
      moduleParams,
      filterRequest
    } );

    // Check for url on the action and navigate
    if( action.url ){
      handleRedirect( { url: action.url, previewDate } );
      return;
    }

    // If there's no url on the action, we expect a graphql call
    if( !invokeAction || !action?.graphql || prevRequestId.current === queryId ){
      return;
    }

    prevRequestId.current = queryId;
    previousAction.current = filterRequest.event;

    action.fetchPolicy = 'no-cache';

    // Execute query
    invokeAction( action );

    // Scroll to top for any filter request besides pagination/user state change (also excludes initAction)
    const shouldScroll =
      filterRequest?.event &&
      ![
        FILTER_EVENT.PAGINATION,
        FILTER_EVENT.USER,
        FILTER_EVENT.LOAD_PREVIOUS,
        FILTER_EVENT.LOAD_MORE,
        FILTER_EVENT.SORT
      ].includes( filterRequest.event );

    if( shouldScroll ){
      scrollToElement( { el: wrapperRef.current, animate: false, autoFocus: false } );
    }

    // Determine item merge/replace strategy
    mergeItems.current = filterRequest?.event === FILTER_EVENT.PAGINATION;
  }, [filterRequest] );

  return (
    <FilterableContentContext.Provider
      value={ {
        setFilterRequest,
        loading,
        mergeItems,
        previousAction
      } }
    >
      <div ref={ wrapperRef }>{ children }</div>
    </FilterableContentContext.Provider>
  );
};

/**
 *
 * @param {object} data Filter action arguments
 * @param {object} data.initAction Filter initAction from meta on initial request
 * @param {object} data.filterRequest Filter request object on filter apply/remove event
 */
export const getFilterAction = ( data ) => {
  const {
    moduleParams = {},
    filterRequest = { action: {} }
  } = data || {};

  const { graphql, url, params, customHeaders, method } = filterRequest?.action || {};

  const action = {
    graphql,
    customHeaders,
    url,
    variables: {},
    method
  };

  // If params is null (not an array) DXL needs all original moduleParams
  if( !Array.isArray( params ) ){
    action.variables.moduleParams = moduleParams;
    return action;
  }

  // If params is an empty array, we don't send anything.
  if( params.length === 0 ){
    return action;
  }

  action.params = params;

  // If we've made it this far, we need to conditionally add moduleParams
  // that DXL is requesting.
  const { minAmount, maxAmount, storeId } = filterRequest;
  const addMinAmount = params.includes( 'minAmount' ) && minAmount;
  const addMaxAmount = params.includes( 'maxAmount' ) && maxAmount;
  const addStoreId = params.includes( 'storeId' ) && storeId;

  // Each entry of this array will add a key/value to moduleParams if the 'shouldAdd' expression
  // evaluates to a truthy value
  const conditions = [
    { shouldAdd: addMinAmount, key: 'minAmount', value: minAmount },
    { shouldAdd: addMaxAmount, key: 'maxAmount', value: maxAmount },
    { shouldAdd: addStoreId, key: 'storeId', value: storeId }
  ];

  action.variables.moduleParams = conditions.reduce( ( acc, { shouldAdd, key, value } ) => {
    if( !shouldAdd ){
      return acc;
    }
    return { ...acc, [key]: value };
  }, {} );

  return action;
};

/**
 * Helper function that compiles a "request id" so we don't submit duplicate consecutive requests
 * @param {object} data Filter request object
 * @param {string} data.id Filter item id
 * @param {string} data.event Filter event type, one of apply/remove from FILTER_EVENT constant
 * @returns {string} stringified ID to compare against previous queries
 */
export const getCompositeFilterQueryId = ( data ) => {
  const {
    filterRequest: { id = 'init', event = 'load' } = {},
    retailerVisitorId = '',
    user: { gti = '', loginType = 'anonymous' } = {}
  } = data || {};
  return `${id}-${event}-${retailerVisitorId}-${gti}-${loginType}`;
};

/**
 * Context object for react reuse
 * @type object
 */
export const FilterableContentContext = createContext();

/**
 * FilterableContentContext provider
 * @type object
 */
export const useFilterableContentContext = () => useContext( FilterableContentContext );

export default FilterableContentProvider;
